/** Shared product facts. Keep dimensions and purchase details out of components. */
export const PRODUCT_STATUS = 'Shipping now';
export const THICKNESS = '1.6 mm';
export const BATTERY_YEARS = 3;
export const CARD_BATTERY_RATING = `Up to ${BATTERY_YEARS} years`;
// Compatibility for sections being updated alongside the shared product model.
export const SPEC_NOTE = '';
export const CLOUD_SYNC_MINUTES = [10, 60] as const;
export const REPORT_INTERVAL = `${CLOUD_SYNC_MINUTES[0]} minutes`;
export const HISTORY_LOOKBACK = '3 months';
export const HISTORY_RETENTION = HISTORY_LOOKBACK;
export const HISTORY_SUMMARY = 'Every location, in order.';
export const SATELLITE_STATUS = 'Satellite check-in, Hubble-enabled';
export const PRODUCT_NAME = 'Latisent Bluetooth LE Card';
export const PRODUCT_DESCRIPTION = 'Slim Bluetooth trackers that remember every place they’ve been seen. Find your things and keep the whole trail.';

export interface Spec { label: string; value: string; note?: string; }
export const SPECS: Spec[] = [
  { label: 'Size', value: `85.5 × 54 × ${THICKNESS}` },
  { label: 'Weight', value: '12 g' },
  { label: 'Battery', value: `${CARD_BATTERY_RATING}, sealed` },
  { label: 'Water rating', value: 'IP68' },
  { label: 'How it fits', value: 'Sleeve or pocket' },
];

export const CARD_PRICE = '$20.99';
export const CARD_PRICE_AMOUNT = Number(CARD_PRICE.replace('$', ''));
export const PURCHASE_SUMMARY = `${CARD_PRICE} per tag, once. App, map and location history included.`;
export const LAUNCH_COVERAGE = 'Where nearby phones or gateways can pick up the tag';
export const VOLUME_MIN_CARDS = 10;
export interface Plan {
  name: string; price: string; cadence: string; blurb: string; features: string[]; cta: string;
  featured?: boolean; contact?: boolean;
}
export const PLANS: Plan[] = [
  { name: 'Every device', price: CARD_PRICE, cadence: 'one time', blurb: 'App, map and location history included.',
    features: ['Location history', 'Arrival and departure alerts', 'Shareable links', 'Everything on one map'], cta: 'Order', featured: true },
  { name: 'For business', price: CARD_PRICE, cadence: 'per device', blurb: 'From a few tools to a whole fleet.',
    features: ['Everything included with one device', 'A shared fleet view', 'Arrival and departure alerts', 'Volume orders available'], cta: 'Order' },
];
export interface FaqItem { question: string; answer: string; }
export const FAQ: FaqItem[] = [
  { question: 'How does it find my tag?', answer: 'A nearby phone or gateway picks up the tag’s Bluetooth signal and sends its own location. Your map shows where the tag was seen nearby and when.' },
  { question: 'How is it different from a Find My or Tile card?', answer: 'Most tracker apps show where a tag was last seen. Latisent keeps every location in order, with arrival and departure alerts, shareable links and everything on one map.' },
  { question: 'How often does the location update?', answer: `About every ${REPORT_INTERVAL} when a nearby phone or gateway picks up the tag. Between updates, your map shows where and when it was last seen.` },
  { question: 'What happens with no phones around?', answer: 'A nearby gateway can also pick up your tag. The Hubble-enabled Puck checks in by satellite. For other tags, your map keeps the last seen location until a phone or gateway finds them again.' },
  { question: 'Do I need to charge it?', answer: `The Card has a sealed battery that lasts ${CARD_BATTERY_RATING.toLowerCase()} without charging. See the lineup for each shape’s battery details.` },
  { question: 'What is included with my tag?', answer: 'Your tag comes with the app, map, location history and network access. Arrival and departure alerts and shareable links are included too.' },
  { question: 'Will it fit in my wallet?', answer: 'The Card slides into a wallet card slot. Metal and RFID-blocking wallets can block Bluetooth, so choose a pocket that lets the signal through.' },
  { question: 'How does location history work?', answer: 'Every location joins your timeline with the time your tag was seen. Scroll back to see the whole trail in order.' },
  { question: 'How do arrival and departure alerts work?', answer: 'Save a place on your map. When a new location shows your tag arriving or leaving, Latisent sends an alert. Alerts arrive as locations update, about every ten minutes when a phone or gateway is near.' },
  { question: 'Can I share a tag’s location?', answer: 'Send a shareable link and choose when it stops working. The map updates as new locations arrive.' },
  { question: 'Which tag has satellite check-in?', answer: 'The Hubble-enabled Puck checks in by satellite, even where no phones are around. The check-in tells you the Puck is active; it does not supply a location.' },
  { question: 'When will my order ship?', answer: 'Latisent is shipping now. We’ll email your order confirmation and delivery estimate. We ship to the US and Canada.' },
];

/** Category benchmarks from market-research.md, November 2026.
 * These ranges cover the researched examples, not every device.
 */
export const COMPETITOR_BASELINE = {
  bluetoothCard: {
    thickness: '1.8 to 2.8 mm',
    battery: 'About 11 to 18 months rechargeable; up to 3 years sealed',
    batteryNote: 'Manufacturer claims for Bluetooth beacon use; not measured by Latisent.',
    devicePrice: 'About $30 to $45 for branded cards; generic bundles can cost less',
    monthly: 'No required subscription',
    deviceRange: [30, 45],
  },
  gpsTracker: {
    thickness: '9.5 to 24 mm',
    battery: 'Tracki: 2 to 3 days at 1 to 5 updates/min; about a month in battery-saver mode',
    batteryNote: 'Invoxia: up to 3 to 4 months.',
    devicePrice: 'About $20 to $159 for Tracki, Jiobit and Invoxia',
    monthly: 'About $3 to $5 for light plans; $8 to $25 for frequent updates',
    monthlyNote: 'Tracki fast-update tiers can reach $25 to $30; prepaid terms and bundles vary.',
    frequentMonthlyRange: 'about $8 to $25 a month',
    // Tracki example from research §2. Do not add monthly fees to Invoxia's
    // bundled device price: it already includes an initial service term.
    trackiDeviceRange: [20, 40],
    trackiMonthlyRange: [9.95, 19.95],
  },
} as const;

/** Two-year ownership costs in USD, from research §1 and §2. */
const comparisonMonths = 24;
const [bluetoothLow, bluetoothHigh] = COMPETITOR_BASELINE.bluetoothCard.deviceRange;
const [gpsDeviceLow, gpsDeviceHigh] = COMPETITOR_BASELINE.gpsTracker.trackiDeviceRange;
const [gpsMonthlyLow, gpsMonthlyHigh] = COMPETITOR_BASELINE.gpsTracker.trackiMonthlyRange;
const gpsTotalLow = gpsDeviceLow + comparisonMonths * gpsMonthlyLow;
const gpsTotalHigh = gpsDeviceHigh + comparisonMonths * gpsMonthlyHigh;
export const TWO_YEAR_COSTS = [
  {
    name: 'Latisent',
    total: CARD_PRICE,
    low: CARD_PRICE_AMOUNT,
    high: CARD_PRICE_AMOUNT,
    note: 'One device purchase. App, map and location history included.',
    featured: true,
  },
  {
    name: 'Bluetooth card',
    total: `About $${bluetoothLow} to $${bluetoothHigh}`,
    low: bluetoothLow,
    high: bluetoothHigh,
    note: 'One branded card purchase. Nearby phones find the tag and send a location.',
    featured: false,
  },
  {
    name: 'GPS tracker · Tracki example',
    total: `About $${Math.round(gpsTotalLow)} to $${Math.round(gpsTotalHigh)}`,
    low: gpsTotalLow,
    high: gpsTotalHigh,
    note: `About $${gpsDeviceLow} to $${gpsDeviceHigh} for the device, plus $${gpsMonthlyLow} to $${gpsMonthlyHigh}/month × ${comparisonMonths}.`,
    featured: false,
  },
] as const;

/** Shared personal and business use cases. */
export const USE_CASE_GROUPS = [
  { name: 'Personal', cases: [
    { id: 'wallet', title: 'Wallets', line: 'Find it before you cancel the cards.' },
    { id: 'luggage', title: 'Luggage', line: 'Know your bag made the flight.' },
    { id: 'family', title: 'Backpacks', line: 'The school bag, wherever it ended up.' },
  ] },
  { name: 'Business', cases: [
    { id: 'freight', title: 'Shipments', line: 'Pickup to delivery, every stop.' },
    { id: 'vehicles', title: 'Vehicles', line: 'Every car on the lot and on the road.' },
    { id: 'tools', title: 'Tools', line: 'Know which site has the good drill.' },
  ] },
] as const;

export type FeatureStatus = 'available';
export type FeatureSupport = 'yes' | 'partial' | 'no';
export interface FeatureCell { value: FeatureSupport; note?: string; }
export interface Feature {
  id: string; label: string; detail: string; status: FeatureStatus;
  latisent: FeatureCell; bluetoothCard: FeatureCell; gpsTracker: FeatureCell;
}
export const FEATURES: readonly Feature[] = [
  { id: 'location-history', label: 'Location history', detail: 'Revisit every location in order.', status: 'available',
    latisent: { value: 'yes', note: HISTORY_SUMMARY }, bluetoothCard: { value: 'no', note: 'Usually shows where a tag was last seen.' }, gpsTracker: { value: 'partial', note: 'History varies by model and plan.' } },
  { id: 'live-tracking', label: 'Near-live updates', detail: 'See new locations on your map.', status: 'available',
    latisent: { value: 'partial', note: `About every ${REPORT_INTERVAL} near a phone or gateway.` }, bluetoothCard: { value: 'partial', note: 'Updates depend on nearby participating phones.' }, gpsTracker: { value: 'yes', note: 'Frequent updates, depending on the model and service.' } },
  { id: 'place-alerts', label: 'Custom alerts and rules', detail: 'Know when a device arrives or leaves.', status: 'available',
    latisent: { value: 'yes', note: 'Alerts arrive as new locations reach your map.' }, bluetoothCard: { value: 'partial', note: 'Availability varies by network.' }, gpsTracker: { value: 'partial', note: 'Availability varies by model.' } },
  { id: 'expiring-links', label: 'Shareable links', detail: 'Send a link and choose when it ends.', status: 'available',
    latisent: { value: 'yes', note: 'Send a link and choose when it ends.' }, bluetoothCard: { value: 'partial', note: 'Sharing options vary by network.' }, gpsTracker: { value: 'partial', note: 'Sharing options vary by plan.' } },
  { id: 'team-map', label: 'Everything on one map', detail: 'See every device together.', status: 'available',
    latisent: { value: 'yes', note: 'Every device together, from a wallet to a fleet.' }, bluetoothCard: { value: 'partial', note: 'Device limits and sharing vary.' }, gpsTracker: { value: 'partial', note: 'Available on selected platforms.' } },
  { id: 'satellite-reporting', label: 'Satellite check-in', detail: 'The Hubble-enabled Puck checks in by satellite.', status: 'available',
    latisent: { value: 'yes', note: 'The Hubble-enabled Puck checks in by satellite where no phones are around.' }, bluetoothCard: { value: 'no', note: 'Relies on nearby phones only.' }, gpsTracker: { value: 'no', note: 'The compared models use cellular service.' } },
];

export const LINEUP = {
  syncMinutes: [10, 60],
  ratingsNote: '',
  satellite: { device: 'Puck', status: 'Available', label: 'Satellite check-in, Hubble-enabled' },
  devices: [
    {
      id: 'card',
      name: 'Card',
      model: 'PB713',
      shape: 'rectangle',
      dimensionsMm: { width: 85.5, height: 54, thickness: 1.6 },
      weight: '12 g',
      batteryRating: 'Up to 3 years',
      batteryType: 'Sealed Li-MnO₂',
      rechargeable: false,
      replaceable: false,
      batteryService: 'No charging. Battery is sealed.',
      ipRating: 'IP68',
      ipNote: '',
      mount: 'Sleeve or pocket',
      bestFor: 'Wallets and passport sleeves',
    },
    {
      id: 'sticker',
      name: 'Sticker',
      model: 'PB708-PRO',
      shape: 'rectangle',
      dimensionsMm: { width: 95, height: 65, thickness: 1.6 },
      weight: 'About 10 g',
      batteryRating: 'Up to 12 months',
      batteryType: 'Li-MnO₂ primary cell',
      rechargeable: false,
      replaceable: null,
      batteryService: 'No charging.',
      ipRating: 'IP65',
      ipNote: '',
      mount: 'Adhesive label',
      bestFor: 'Boxes, pallets and cases',
    },
    {
      id: 'puck',
      name: 'Puck',
      model: 'PB705',
      shape: 'round',
      dimensionsMm: { width: 42.5, height: 42.5, thickness: 24 },
      weight: '42.6 g',
      batteryRating: 'Up to 36 months',
      batteryType: 'CR14250',
      rechargeable: false,
      replaceable: true,
      batteryService: 'Replaceable battery. No charging.',
      ipRating: 'IP68',
      ipNote: 'With covers correctly closed',
      mount: 'Adhesive or magnetic',
      bestFor: 'Vehicles, trailers and tools',
    },
    {
      id: 'mini',
      name: 'Mini tag',
      model: 'PB703',
      shape: 'rectangle',
      dimensionsMm: { width: 48, height: 38, thickness: 13.7 },
      weight: '39 g',
      batteryRating: 'About 30 months',
      batteryType: 'Disposable primary cell',
      rechargeable: false,
      replaceable: null,
      batteryService: 'No charging.',
      ipRating: 'IP54',
      ipNote: '',
      mount: 'Adhesive',
      bestFor: 'Keys and bags',
    },
  ],
} as const;
